/**
 * Hyly Attribution Block
 *
 * Injection block that loads the hy.ly attribution and marketing scripts.
 * Site-wide: same ssid across all properties.
 */

import { useEffect, useRef } from 'react';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken } from '@vibesites/runtime/react';
import { useTrackingAllowed } from '../../shared/analytics';

const SCRIPT_ID = 'hyly-attribution-script';

function HylyAttributionComponent(_props: BlockProps) {
  const ssid = useBlockToken('ssid', { defaultValue: '' });
  const trackingAllowed = useTrackingAllowed('advertising');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!trackingAllowed || !ssid) return;

    if (document.getElementById(SCRIPT_ID)) return;

    // Load hy.ly attribution manager
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = 'https://my.hy.ly/js/attribution-manager.js?v=1.2';
    script.async = true;
    document.head.appendChild(script);

    // Load event manager
    const eventScript = document.createElement('script');
    eventScript.src = 'https://my.hy.ly/js/event-manager.js?v=1.2';
    eventScript.async = true;
    document.head.appendChild(eventScript);

    // Chat iframe (hidden, used for tracking)
    const iframe = document.createElement('iframe');
    iframe.src = `https://my.hy.ly/chat/${ssid}?page_url=${encodeURIComponent(window.location.origin)}`;
    iframe.title = 'Hyly Attribution Tracking';
    iframe.className = 'hyly-gtm';
    iframe.style.cssText = 'width:1px;height:1px;visibility:hidden;position:absolute;bottom:0;left:0;';
    document.body.appendChild(iframe);

    return () => {
      mountedRef.current = false;
      const el = document.getElementById(SCRIPT_ID);
      if (el) el.remove();
      const iframeEl = document.querySelector('.hyly-gtm');
      if (iframeEl) iframeEl.remove();
    };
  }, [trackingAllowed, ssid]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'HylyAttribution',
  displayName: 'Hyly Attribution',
  tokens: {
    ssid: {
      label: 'SSID',
      type: 'text',
      description: 'hy.ly session/site identifier.',
      required: true,
    },
  },
};

export const hylyAttributionBlock: BlockDefinition = {
  id: 'hyly-attribution',
  name: 'Hyly Attribution',
  description: 'hy.ly marketing attribution and event tracking for lead source identification.',
  category: 'Analytics',
  placement: 'injection',
  defaultScope: 'global',
  component: HylyAttributionComponent,
  contentSchema,
  tags: ['hyly', 'attribution', 'marketing', 'tracking', 'analytics'],
};
